import { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import CustomerService from '@/services/CustomerService'
import { ChatBubbleLeftRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useNotifications } from '@/hooks/useNotifications'

const LOAD_FAILED_KEY = 'customers.details.communicationPanel.loadFailed'
const ADD_FAILED_KEY = 'customers.details.communicationPanel.addFailed'

export default function CommunicationPanel({ customerId, initialCommunications }) {
	const { t } = useTranslation()
	const [communications, setCommunications] = useState(() =>
		Array.isArray(initialCommunications) ? initialCommunications : []
	)
	const [newNote, setNewNote] = useState('')
	const [addingNote, setAddingNote] = useState(false)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [message, setMessage] = useState(null)
	const hasCustomer = Boolean(customerId)

	useNotifications({
		error,
		message,
		clearError: () => setError(null),
		clearMessage: () => setMessage(null),
	})

	const canAdd = Boolean(newNote.trim()) && !addingNote && hasCustomer

	const hydrateCommunications = useCallback(
		async (suppressErrors = false) => {
			if (!customerId) return
			setLoading(true)
			try {
				const { data, error } = await CustomerService.getCustomerById(customerId)
				if (error) throw new Error(error)
				const list = Array.isArray(data?.communications) ? data.communications : []
				// Sort by added date descending
				list.sort((a, b) => {
					const da = a?.added ? new Date(a.added).getTime() : 0
					const db = b?.added ? new Date(b.added).getTime() : 0
					return db - da
				})
				setCommunications(list)
			} catch (err) {
				if (!suppressErrors) {
					setError(err.message || LOAD_FAILED_KEY)
				}
			} finally {
				setLoading(false)
			}
		},
		[customerId]
	)

	useEffect(() => {
		if (!hasCustomer) {
			setCommunications([])
			return
		}
		hydrateCommunications(true)
	}, [hasCustomer, hydrateCommunications])

	useEffect(() => {
		if (!initialCommunications) return
		setCommunications(Array.isArray(initialCommunications) ? initialCommunications : [])
	}, [initialCommunications])

	const handleAddNote = useCallback(async () => {
		const value = newNote.trim()
		if (!value || addingNote || !customerId) return

		setAddingNote(true)
		try {
			const payload = { id: customerId, note: value, added: moment().toISOString() }
			const { success, error } = await CustomerService.addCommunication(payload)
			if (!success) throw new Error(error || ADD_FAILED_KEY)
			await hydrateCommunications()
			setNewNote('')
		} catch (err) {
			setError(err.message || ADD_FAILED_KEY)
		} finally {
			setAddingNote(false)
		}
	}, [addingNote, customerId, hydrateCommunications, newNote])

	return (
		<div className="flex h-[525px] max-h-[483px] w-full flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
			{/* Header */}
			<div className="border-b border-gray-300 bg-gray-100 px-6 py-3">
				<div className="flex items-start gap-3 text-gray-700">
					<h2 className="flex items-center gap-2 text-md font-semibold text-gray-900">
						<ChatBubbleLeftRightIcon className="h-5 w-5 text-sky-700" />
						<span>{t('customers.details.communicationPanel.title')}</span>
					</h2>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto px-3 py-3 sm:px-4 mr-4 sm:mr-2">
				{/* Add Note Section */}
				<div className="bg-white p-2">
					<div className="space-y-3">
						<textarea
							value={newNote}
							onChange={e => setNewNote(e.target.value)}
							rows={2}
							disabled={!hasCustomer}
							className="block w-full resize-none rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-sm text-gray-900 shadow-inner placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-70"
							placeholder={t('customers.details.communicationPanel.addPlaceholder')}
						/>
						<div className="grid grid-cols-1 gap-3">
							<button
								type="button"
								onClick={handleAddNote}
								disabled={!canAdd}
								className="inline-flex items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
								{addingNote ? t('customers.details.communicationPanel.addingNote') : t('customers.details.communicationPanel.addNote')}
							</button>
						</div>
					</div>
				</div>

				{/* Communications List */}
				<div className="mt-4 mb-4 space-y-4">
					{!hasCustomer ? (
						<div className="py-8 text-center text-gray-500">
							<p className="text-sm">{t('customers.details.communicationPanel.selectPrompt')}</p>
						</div>
					) : loading ? (
						<div className="py-8 text-center text-gray-500">
							<p className="text-sm">{t('customers.details.communicationPanel.loading')}</p>
						</div>
					) : communications.length > 0 ? (
						<div className="relative space-y-5">
							<div className="absolute left-5 top-0 h-full w-px bg-gray-200" aria-hidden="true" />
							{communications.map((c, idx) => {
								const key = c._id || `${c.added ?? 'na'}-${idx}`
								return (
									<div key={key} className="relative flex items-start gap-4 pl-12">
										<div className="absolute left-1 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-blue-600 shadow-sm">
											<ChatBubbleLeftRightIcon className="h-5 w-5" />
										</div>
											<div className="w-full rounded-2xl border border-gray-200 bg-white pl-4 pr4 pt-3 pb-3 shadow-sm">
												<p className="text-sm font-semibold text-gray-900">
													{moment(c?.added).format('YYYY-MM-DD HH:mm')} {t('customers.details.communicationPanel.byUser', { user: c?.userName || t('customers.details.communicationPanel.unknownUser') })}
												</p>
												<p className="mt-1 text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">{c?.note ?? ''}</p>
											</div>
									</div>
								)
							})}
						</div>
					) : (
						<div className="flex min-h-[155px] items-center justify-center gap-3 rounded-md bg-white px-4 pt-5 text-sm text-slate-600">
							<ChatBubbleLeftRightIcon className="h-5 w-5 text-slate-400" />
							<span>{t('customers.details.communicationPanel.noHistory')}</span>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
